import React, { useEffect, useMemo, useState } from 'react'
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { estoqueService } from '../../services/estoque'
import type { EstoqueLocal } from '../../types/estoque'
import type { MarketingInvoice, MarketingInvoiceAllocationInput } from '../../types/marketingNotas'
import { formatQuantity } from '../Estoque/estoqueUi'
import { SELECT_CLASS } from './notasUi'

type AllocationItem = {
  invoice_item_id: string
  stock_item_id: string
  label: string
  quantity: number
  unit?: string
  lines: { location_id: string; quantity: number }[]
}

type Props = {
  open: boolean
  invoice: MarketingInvoice | null
  busy?: boolean
  onClose: () => void
  onConfirm: (allocations: MarketingInvoiceAllocationInput[]) => void | Promise<void>
}

const NotasConfirmEntradaModal: React.FC<Props> = ({
  open,
  invoice,
  busy = false,
  onClose,
  onConfirm,
}) => {
  const [items, setItems] = useState<AllocationItem[]>([])
  const [locais, setLocais] = useState<EstoqueLocal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !invoice) return

    const mapped: AllocationItem[] = (invoice.items || []).map((item) => ({
      invoice_item_id: item.id,
      stock_item_id: item.stock_item_id || '',
      label:
        item.stock_item
          ? `${item.stock_item.code} — ${item.stock_item.name}`
          : item.description || 'Item',
      quantity: Number(item.quantity),
      unit: item.stock_item?.unit,
      lines: [{ location_id: '', quantity: Number(item.quantity) }],
    }))
    setItems(mapped)
    setError(null)

    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const locaisData = await estoqueService.listLocais(false)
        if (cancelled) return
        setLocais(locaisData.filter((l) => l.is_active))
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar locais de estoque.')
          setLocais([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, invoice])

  const validationError = useMemo(() => {
    for (const item of items) {
      if (!item.lines.length) return `Informe ao menos um local para "${item.label}".`
      let sum = 0
      for (const line of item.lines) {
        if (!line.location_id) return `Selecione o local para "${item.label}".`
        if (!(line.quantity > 0)) return `Quantidade inválida em "${item.label}".`
        sum += line.quantity
      }
      const uniqueLocais = new Set(item.lines.map((l) => l.location_id))
      if (uniqueLocais.size !== item.lines.length) {
        return `Não repita o mesmo local em "${item.label}".`
      }
      if (Math.abs(sum - item.quantity) > 0.0005) {
        return `A soma das quantidades de "${item.label}" deve ser ${formatQuantity(item.quantity)}.`
      }
    }
    return null
  }, [items])

  if (!open || !invoice) return null

  const updateLine = (
    itemIndex: number,
    lineIndex: number,
    patch: Partial<{ location_id: string; quantity: number }>
  ) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item
        return {
          ...item,
          lines: item.lines.map((line, j) => (j === lineIndex ? { ...line, ...patch } : line)),
        }
      })
    )
  }

  const addLine = (itemIndex: number) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item
        return {
          ...item,
          lines: [...item.lines, { location_id: '', quantity: 0 }],
        }
      })
    )
  }

  const removeLine = (itemIndex: number, lineIndex: number) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item
        if (item.lines.length <= 1) return item
        return {
          ...item,
          lines: item.lines.filter((_, j) => j !== lineIndex),
        }
      })
    )
  }

  const handleConfirm = async () => {
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    const allocations: MarketingInvoiceAllocationInput[] = []
    for (const item of items) {
      for (const line of item.lines) {
        allocations.push({
          invoice_item_id: item.invoice_item_id,
          location_id: line.location_id,
          quantity: line.quantity,
        })
      }
    }
    await onConfirm(allocations)
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => !busy && onClose()} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              Confirmar entrada — NF-e {invoice.number}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              Informe para qual local de estoque vai cada item. É possível dividir a quantidade em
              mais de um local.
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {(error || validationError) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error || validationError}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : items.length === 0 ? (
            <Text size="sm" color="muted">
              Nenhum item para alocar.
            </Text>
          ) : (
            items.map((item, itemIndex) => {
              const allocated = item.lines.reduce(
                (acc, line) => acc + (Number(line.quantity) || 0),
                0
              )
              return (
                <div
                  key={item.invoice_item_id}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                        {item.label}
                      </Text>
                      <Text size="xs" color="muted" className="mt-0.5">
                        Total a entrar: {formatQuantity(item.quantity)}
                        {item.unit ? ` ${item.unit}` : ''} · Alocado: {formatQuantity(allocated)}
                      </Text>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addLine(itemIndex)}
                      disabled={busy || loading}
                      className="inline-flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Outro local
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {item.lines.map((line, lineIndex) => (
                      <div
                        key={`${item.invoice_item_id}-${lineIndex}`}
                        className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px_auto]"
                      >
                        <select
                          className={SELECT_CLASS + ' w-full'}
                          value={line.location_id}
                          disabled={busy || loading}
                          onChange={(e) =>
                            updateLine(itemIndex, lineIndex, { location_id: e.target.value })
                          }
                        >
                          <option value="">Selecione o local</option>
                          {locais.map((local) => (
                            <option key={local.id} value={local.id}>
                              {local.name}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={Number.isFinite(line.quantity) ? line.quantity : ''}
                          disabled={busy || loading}
                          onChange={(e) =>
                            updateLine(itemIndex, lineIndex, {
                              quantity: e.target.value === '' ? 0 : Number(e.target.value),
                            })
                          }
                        />
                        <button
                          type="button"
                          onClick={() => removeLine(itemIndex, lineIndex)}
                          disabled={busy || item.lines.length <= 1}
                          className="inline-flex items-center justify-center rounded-lg px-2 py-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-900/20"
                          title="Remover local"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="solid"
            onClick={() => void handleConfirm()}
            disabled={busy || loading || Boolean(validationError) || items.length === 0}
          >
            {busy ? 'Confirmando...' : 'Confirmar entrada no estoque'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default NotasConfirmEntradaModal
